import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils/cn'

/**
 * Root provider. Mount once near the app root; children Tooltips reuse it.
 * Default delay 500ms per 03c spec.
 */
export function TooltipProvider({
  children,
  delayDuration = 500,
  skipDelayDuration = 200,
}: {
  children: React.ReactNode
  delayDuration?: number
  skipDelayDuration?: number
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration}>
      {children}
    </TooltipPrimitive.Provider>
  )
}

export interface TooltipProps {
  /** The anchor element — usually a button or icon-only trigger. */
  children: React.ReactNode
  /** Tooltip body copy. Keep short — never carry critical info. */
  label: React.ReactNode
  /** Optional keyboard shortcut chip rendered alongside the label. */
  shortcut?: string
  /** Disable without unmounting — useful for conditional tooltips. */
  disabled?: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
}

/**
 * Always call inside a <TooltipProvider>. Wraps a single trigger element —
 * uses Radix's `asChild` so no extra wrapper DOM is introduced.
 */
export function Tooltip({
  children,
  label,
  shortcut,
  disabled,
  side = 'top',
  align = 'center',
  sideOffset = 8,
}: TooltipProps) {
  if (disabled) return <>{children}</>
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'z-tooltip inline-flex items-center gap-1.5 rounded-xs bg-bg-inverse px-2 py-1 text-2xs font-medium text-text-inverse shadow-md',
            'animate-in fade-in-0 zoom-in-95 duration-fast',
          )}
        >
          <span>{label}</span>
          {shortcut ? (
            <span
              className="text-role-kbd-shortcut inline-flex items-center rounded-xs px-1 opacity-80"
              style={{ background: 'var(--color-kbd-bg)' }}
            >
              {shortcut}
            </span>
          ) : null}
          <TooltipPrimitive.Arrow className="fill-bg-inverse" width={8} height={4} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
